// arise-frontend/src/services/api.js
// Aligned with the authenticated job-based backend (main.py):
//   POST /upload -> job_id, GET /analysis/{job_id}, POST /report/generate
//   POST /auth/login, POST /auth/register, GET /auth/me, POST /auth/logout
// Legacy /upload-analyze, /task-status/{id}, /task/{id} (Celery) are no longer used.
// API base URL (REACT_APP_API_URL is inlined at build time). Development and
// tests default to the local backend. A production build without it must not
// silently call the visitor's own localhost: it targets a reserved, never-
// resolving .invalid host so every request fails visibly ("Cannot reach the
// ARISE server"), and the misconfiguration is logged once.
export const API_URL_NOT_CONFIGURED = 'https://api-url-not-configured.invalid';

export function resolveApiBaseUrl(env = process.env) {
  const configured = (env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (configured) return configured;
  return env.NODE_ENV === 'production' ? API_URL_NOT_CONFIGURED : 'http://localhost:8000';
}

const BASE_URL = resolveApiBaseUrl();
if (BASE_URL === API_URL_NOT_CONFIGURED) {
  // eslint-disable-next-line no-console
  console.error('AutoRev: REACT_APP_API_URL is not set for this production build; API calls will fail.');
}

// ── Auth token handling ──────────────────────────────────────────────────────
// The token is issued by POST /auth/login (Token.access_token). It is never
// hardcoded. All token reads/writes in the app go through the functions below.
//
// SECURITY NOTE: the token lives in localStorage, so any XSS on this origin can
// read it. The backend JWT is stateless and short-lived (30 min) and
// POST /auth/logout does NOT revoke it server-side: clearing it here ends the
// session in this browser only.
export const TOKEN_STORAGE_KEY = 'arise_access_token';

export function getAccessToken() {
  try { return window.localStorage.getItem(TOKEN_STORAGE_KEY) || null; } catch { return null; }
}
export function setAccessToken(token) {
  try { window.localStorage.setItem(TOKEN_STORAGE_KEY, token); } catch {}
}
export function clearAccessToken() {
  try { window.localStorage.removeItem(TOKEN_STORAGE_KEY); } catch {}
}

// ── Errors ───────────────────────────────────────────────────────────────────
// Backend error shapes: HTTPException -> {detail}, validation -> {detail:[{loc,msg}]},
// global handler -> {error, details}, analysis routes -> HTTP 200 {error}.
// ApiError keeps the HTTP status + parsed body so callers can branch on them.
// `message` keeps the historical "<prefix>: <text>" format.
export class ApiError extends Error {
  constructor(message, { status = 0, body = null, userMessage = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.userMessage = userMessage;
  }
}

function errorText(body, fallback) {
  if (!body) return fallback;
  const d = body.detail ?? body.error ?? body.details;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d.map(x => x && x.msg).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  return JSON.stringify(d);
}

async function failFromResponse(res, prefix) {
  let body = null;
  try { body = await res.json(); } catch {}
  let msg = errorText(body, res.statusText || `HTTP ${res.status}`);
  if (res.status === 401) msg = `${msg} (please log in again)`;
  throw new ApiError(`${prefix}: ${msg}`, { status: res.status, body });
}

/** FastAPI 422 -> { fieldName: message } using the last element of each `loc`. */
export function fieldErrorsFrom(err) {
  const d = err && err.body && err.body.detail;
  if (!Array.isArray(d)) return {};
  const out = {};
  for (const item of d) {
    const loc = item && Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null;
    if (typeof loc === 'string' && !out[loc]) out[loc] = String((item && item.msg) || 'Invalid value');
  }
  return out;
}

/** Friendly, non-technical text for auth-form failures. Never shows raw backend text. */
export function authErrorMessage(err, kind = 'login') {
  if (err && err.userMessage) return err.userMessage;
  const s = err && err.status;
  if (s === 429) return 'Too many attempts. Please wait a minute and try again.';
  if (s === 401) return kind === 'login' ? 'Invalid email or password.' : 'Please log in to continue.';
  if (s === 403) return 'This account is inactive.';
  if (s === 409) return 'We could not create an account with those details. The email or username may already be in use.';
  if (s === 422) return 'Some details are not valid. Please check the highlighted fields.';
  if (s >= 500) return 'The server ran into a problem. Please try again shortly.';
  return 'Something went wrong. Please try again.';
}

// ── Session lifecycle (central 401 handling + in-flight isolation) ───────────
const inflight  = new Set();           // AbortControllers of authenticated requests
const listeners = new Set();           // session-expired subscribers
let sessionEpoch = 0;                  // bumped whenever the session ends/changes

function abortInflight() {
  sessionEpoch += 1;
  for (const c of [...inflight]) c.abort();
  inflight.clear();
}

/** Subscribe to "protected request got 401 with the current token". Returns unsubscribe. */
export function onSessionExpired(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function expireSession() {
  clearAccessToken();
  abortInflight();
  listeners.forEach(fn => { try { fn(); } catch {} });
}

/** Local logout: drop the token and abort every private in-flight request. */
export function discardSession() {
  clearAccessToken();
  abortInflight();
}

/** Abort private in-flight requests without touching storage (e.g. token changed in another tab). */
export function abortPrivateRequests() {
  abortInflight();
}

/**
 * Authenticated request wrapper. Attaches the Bearer token, tracks the request
 * so logout can abort it, runs `consume(res)` to fully read the response, and
 * only then applies the 401 rule: expire the session if (and only if) the 401
 * was produced for the token that is still current. That keeps stale 401s from
 * an earlier session from logging out a newer one, and fires exactly once.
 * 403, 404, 429, 5xx and network failures never clear the session.
 */
async function authRequest(url, init, consume, extSignal) {
  const token = getAccessToken();
  const controller = new AbortController();
  if (extSignal) {
    if (extSignal.aborted) controller.abort();
    else extSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  inflight.add(controller);
  let res = null;
  try {
    const headers = { ...((init && init.headers) || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    res = await fetch(url, { ...init, headers, signal: controller.signal });
    return await consume(res);
  } finally {
    inflight.delete(controller);
    if (res && res.status === 401 && token && getAccessToken() === token) expireSession();
  }
}

// ── Auth endpoints ───────────────────────────────────────────────────────────

async function publicPost(path, payload, prefix) {
  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new ApiError(`${prefix}: network error`, {
      status: 0,
      userMessage: 'Cannot reach the ARISE server. Check your connection and try again.',
    });
  }
  if (!res.ok) await failFromResponse(res, prefix);
  return res.json();
}

/** Log in, store the token centrally, resolve the public user object. A 401 here
 *  means wrong credentials, NOT an expired session, so it never touches stored state. */
export async function login(email, password) {
  const data = await publicPost('/auth/login', { email, password }, 'Login failed');
  if (!data || !data.access_token) {
    throw new ApiError('Login failed: server returned no access token', { status: 502 });
  }
  abortInflight();                          // isolate any previous session's requests
  setAccessToken(data.access_token);
  if (getAccessToken() !== data.access_token) {
    throw new ApiError('Login failed: token could not be stored', {
      status: 0,
      userMessage: 'Your browser blocked storing the login session. Enable site storage and try again.',
    });
  }
  return data.user || null;
}

/** Register. Accepts { email, username, password, full_name? }. Never stores anything. */
export async function register(payload) {
  return publicPost('/auth/register', payload, 'Registration failed');
}

/** GET /auth/me with the stored token. 401 -> session expired (handled centrally). */
export async function fetchCurrentUser() {
  return authRequest(`${BASE_URL}/auth/me`, {}, async res => {
    if (!res.ok) await failFromResponse(res, 'Session check failed');
    return res.json();
  });
}

/** Best-effort POST /auth/logout. Never throws, never blocks local logout.
 *  Note: the backend does not revoke the JWT; this is a lifecycle ping only. */
export function remoteLogout(token) {
  if (!token) return Promise.resolve();
  const c = new AbortController();
  const t = setTimeout(() => c.abort(), 5000);
  return fetch(`${BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    signal: c.signal,
  }).then(() => {}, () => {}).finally(() => clearTimeout(t));
}

// ── Upload + analyse ─────────────────────────────────────────────────────────

// The analysis runs in a backend worker; the browser only polls. Giving up
// waiting does NOT stop or lose the server-side job (it stays in job history).
const ANALYSIS_WAIT_MS = 30 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;
const activeControllers = new Map();   // job_id -> AbortController

// Backend pipeline stage -> [dashboard stage, pipeline milestone %]. The
// percentages are the pipeline's own stage milestones, not a time estimate.
const BACKEND_STAGES = {
  queued:               ['extraction', 2],
  extraction:           ['extraction', 10],
  function_analysis:    ['behaviour', 30],
  capa:                 ['capability', 50],
  behaviour_capability: ['capability', 65],
  mitre:                ['mitre', 80],
  scoring:              ['risk', 95],
  complete:             ['verdict', 99],
};

export function progressFromJob(job) {
  const key = job && (job.stage || (job.status === 'queued' || job.status === 'uploaded' ? 'queued' : null));
  return BACKEND_STAGES[key] || ['extraction', 2];
}

/** GET /jobs/{id}: owner-scoped job status (no result payload). */
export async function fetchJobStatus(jobId, signal) {
  return authRequest(`${BASE_URL}/jobs/${encodeURIComponent(jobId)}`, {}, async res => {
    if (!res.ok) await failFromResponse(res, 'Job status failed');
    return res.json();
  }, signal);
}

/** GET /jobs: the signed-in user's recent jobs (newest first). */
export async function listJobs(signal) {
  return authRequest(`${BASE_URL}/jobs`, {}, async res => {
    if (!res.ok) await failFromResponse(res, 'Job history failed');
    const data = await res.json();
    return Array.isArray(data && data.jobs) ? data.jobs : [];
  }, signal);
}

// ── Billing (read-only; purchases are not available until a payment provider
//    is integrated and approved) ─────────────────────────────────────────────

/** GET /billing/plans (public): authoritative prices, amounts in minor units. */
export async function getBillingPlans(signal) {
  let res;
  try {
    res = await fetch(`${BASE_URL}/billing/plans`, { signal });
  } catch (e) {
    if (e && e.name === 'AbortError') throw e;
    throw new ApiError('Plans failed: network error', { status: 0 });
  }
  if (!res.ok) await failFromResponse(res, 'Plans failed');
  return res.json();
}

/** GET /billing/entitlements: the signed-in user's quota, credits, subscription. */
export async function getEntitlements(signal) {
  return authRequest(`${BASE_URL}/billing/entitlements`, {}, async res => {
    if (!res.ok) await failFromResponse(res, 'Entitlements failed');
    return res.json();
  }, signal);
}

/** GET /billing/usage: the signed-in user's charges (newest first). */
export async function getUsage(signal) {
  return authRequest(`${BASE_URL}/billing/usage`, {}, async res => {
    if (!res.ok) await failFromResponse(res, 'Usage failed');
    const data = await res.json();
    return Array.isArray(data && data.usage) ? data.usage : [];
  }, signal);
}

/** "₹49.00" from (4900, "INR"); null when the backend supplied no price. */
export function formatPrice(minor, currency) {
  if (typeof minor !== 'number' || !Number.isFinite(minor) || !currency) return null;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) signal.addEventListener('abort', () => {
      clearTimeout(t);
      reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
    }, { once: true });
  });
}

function uploadFile(file, signal) {
  const formData = new FormData();
  formData.append('file', file);
  // no Content-Type: browser sets multipart boundary
  return authRequest(`${BASE_URL}/upload`, { method: 'POST', body: formData }, async res => {
    if (!res.ok) await failFromResponse(res, 'Upload failed');
    const data = await res.json();
    if (!data.job_id) throw new Error('Upload failed: server returned no job_id');
    return data;
  }, signal);
}

/** GET /analysis/{id}: { pending: true, job } while running (HTTP 202),
 *  otherwise { pending: false, data } with the persisted result. */
function fetchAnalysis(jobId, signal) {
  return authRequest(`${BASE_URL}/analysis/${encodeURIComponent(jobId)}`, {}, async res => {
    if (res.status === 202) return { pending: true, job: await res.json() };
    if (!res.ok) await failFromResponse(res, 'Analysis failed');
    const data = await res.json();
    if (data && data.error) throw new Error(`Analysis failed: ${errorText(data, 'unknown error')}`);
    return { pending: false, data };
  }, signal);
}

/**
 * Poll an existing job until its persisted result is available.
 * Resolves the raw backend result. Used for new uploads and for reopening
 * a job from history after a refresh.
 */
export async function waitForAnalysis(jobId, onProgress, options = {}) {
  const signal = options.signal;
  const interval = options.pollIntervalMs ?? POLL_INTERVAL_MS;
  for (;;) {
    const out = await fetchAnalysis(jobId, signal);
    if (!out.pending) return out.data;
    if (onProgress) {
      const [stage, pct] = progressFromJob(out.job);
      onProgress(stage, pct);
    }
    await sleep(interval, signal);
  }
}

/**
 * Upload a file and wait for its background analysis.
 * Resolves { result, taskId } where taskId is the backend job_id.
 * onProgress(stage, pct) reports the backend's real pipeline stage.
 * Ending the session (logout / 401 / token change) stops polling and the
 * result is discarded ("Analysis cancelled"); the server-side job continues
 * and remains available from job history.
 */
export async function analyzeFile(file, onProgress, options = {}) {
  const started    = Date.now();
  const epoch      = sessionEpoch;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ANALYSIS_WAIT_MS);
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let jobId = null;
  try {
    if (onProgress) onProgress('extraction', 1);
    const upload = await uploadFile(file, controller.signal);
    jobId = upload.job_id;
    activeControllers.set(jobId, controller);
    if (options.onJobCreated) options.onJobCreated(jobId);

    const raw = await waitForAnalysis(jobId, onProgress, {
      signal: controller.signal, pollIntervalMs: options.pollIntervalMs,
    });
    if (epoch !== sessionEpoch) throw new Error('Analysis cancelled');
    const elapsed = (Date.now() - started) / 1000;
    const result = normaliseAnalysis(raw, upload, elapsed);
    if (onProgress) onProgress('verdict', 100);
    return { result, taskId: jobId };
  } catch (e) {
    if (e && e.name === 'AbortError') {
      throw new Error(timedOut
        ? 'Still running after 30 minutes. The analysis continues on the server — reopen it later from your job history.'
        : 'Analysis cancelled');
    }
    throw e;
  } finally {
    clearTimeout(timer);
    if (jobId) activeControllers.delete(jobId);
  }
}

/** Stops waiting in this browser only: the backend has no cancel endpoint, so
 *  the server-side job keeps running. Kept so existing imports keep working. */
export async function cancelTask(jobId) {
  const c = activeControllers.get(jobId);
  if (c) c.abort();
}

// ── Result normalisation: backend pipeline result -> shape DashboardPage reads ──

const asArray = v => (Array.isArray(v) ? v : []);
const uniq    = arr => [...new Set(arr)];

function toMitreIds(finalMitre) {
  const list = Array.isArray(finalMitre)
    ? finalMitre
    : asArray(finalMitre && (finalMitre.mitre_techniques || finalMitre.final_mitre));
  return uniq(
    list
      .map(t => (typeof t === 'string' ? t : t && (t.technique_id || t.id || t.technique || t.mitre_id)))
      .filter(Boolean)
      .map(String)
  );
}

// Unconfirmed ATT&CK candidates (capability- or CAPA-derived hypotheses).
// Never merged into `mitre` (evidence-supported findings).
const ATTACK_ID = /^T\d{4}(\.\d{3})?$/;
const strList = v => asArray(v).filter(x => typeof x === 'string');

export function toMitreCandidates(finalMitre) {
  const details = finalMitre && typeof finalMitre === 'object' ? asArray(finalMitre.candidate_details) : [];
  const seen = new Set();
  const out = [];
  for (const d of details) {
    const id = d && d.technique;
    if (typeof id !== 'string' || !ATTACK_ID.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({
      technique: id,
      sources: strList(d.sources),
      derived_from_capabilities: strList(d.derived_from_capabilities),
      derived_from_capa_rules: strList(d.derived_from_capa_rules),
    });
  }
  return out;
}

function toVerdict(v) {
  const o = (typeof v === 'string') ? { verdict: v } : (v && typeof v === 'object' ? { ...v } : {});
  const label = o.verdict || o.label || 'UNKNOWN';
  o.verdict = label;
  o.label   = o.label || label;
  return o;
}

export function normaliseAnalysis(raw, upload = {}, elapsedSeconds) {
  raw = raw || {};
  upload = upload || {};
  const analysis = raw.analysis || {};
  const fnResults = asArray(analysis.results);
  const risk = raw.risk || {};
  const cvssRes = raw.cvss_results || {};
  const meta = raw.analysis_meta || {};
  const behaviours = raw.behaviors || raw.behaviour || {};
  const verdict = toVerdict(raw.arise_verdict);

  const functions = fnResults.map(f => {
    if (typeof f === 'string') return { name: f, size: 0 };
    return {
      name: f.name || f.function || f.function_name || f.address || f.offset || 'unknown',
      size: f.size ?? f.length ?? 0,
    };
  });

  const calls = uniq(
    fnResults.flatMap(f => asArray(f && (f.calls || f.api_calls || f.apis)))
      .filter(c => typeof c === 'string')
  );

  return {
    functions,
    imports: asArray(raw.imports),
    strings: asArray(raw.strings),
    calls,
    behaviours: asArray(behaviours.behaviors),
    capabilities: asArray(raw.capabilities),
    mitre: toMitreIds(raw.final_mitre),
    mitre_candidates: toMitreCandidates(raw.final_mitre),
    risk_score: Number(risk.combined_score) || 0,
    cvss: Number(cvssRes.cvss_score) || 0,
    cvss_detail: cvssRes,
    verdict,
    // Upload response only provides sha256; md5/sha1 render as "N/A".
    hashes: upload.sha256 ? { sha256: upload.sha256 } : {},
    file_type: upload.file_type || '',
    summary: verdict.summary || '',
    analysis_meta: {
      ...meta,
      filename: upload.filename || meta.filename,
      total_elapsed: elapsedSeconds,
    },
  };
}

/** Reopen a job from history: waits for its persisted result and normalises it.
 *  Upload-only metadata (sha256, file type) is not available on this path. */
export async function openJob(job, onProgress, options = {}) {
  const raw = await waitForAnalysis(job.job_id, onProgress, options);
  return {
    result: normaliseAnalysis(raw, { filename: job.filename }),
    taskId: job.job_id,
  };
}

// ── Report download ──────────────────────────────────────────────────────────

export async function downloadReport(taskId, sections = null) {
  if (!taskId || taskId === 'demo') {
    throw new Error('Report download is not available in demo mode.');
  }
  // taskId is the job_id in the current architecture.
  const body = { job_id: taskId };
  if (sections) body.sections = sections;
  const epoch = sessionEpoch;

  return authRequest(`${BASE_URL}/report/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }, async res => {
    if (!res.ok) await failFromResponse(res, 'Report generation failed');

    const blob     = await res.blob();
    // Never save a private report after the session ended or changed.
    if (epoch !== sessionEpoch) throw new Error('Report download cancelled');
    const cd       = res.headers.get('Content-Disposition') || '';
    const match    = cd.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : 'ARISE_Report.pdf';

    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return filename;
  });
}

export async function getReportSections() {
  try {
    return await authRequest(`${BASE_URL}/report/sections`, {}, res => (res.ok ? res.json() : null));
  } catch { return null; }
}

// ── Chat (authenticated; the client supplies the analysis, it is NOT job-bound) ──
const CHAT_PATHS = { summary: '/chat-summary', mitre: '/chat-mitre' };

export async function sendChat(kind, payload) {
  const path = CHAT_PATHS[kind];
  if (!path) throw new Error(`Chat failed: unknown chat type "${kind}"`);
  return authRequest(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }, async res => {
    if (!res.ok) await failFromResponse(res, 'Chat failed');
    return res.json();
  });
}

export async function healthCheck() {
  try {
    const res = await fetch(`${BASE_URL}/health`);
    return res.ok;
  } catch { return false; }
}

// ── Mock result for demo mode ────────────────────────────────────────────────

export const MOCK_RESULT = {
  functions: [
    { name: 'sub_401000', size: 128 }, { name: 'CreateRemoteThread', size: 64 },
    { name: 'VirtualAllocEx', size: 96 }, { name: 'WriteProcessMemory', size: 80 },
    { name: 'GetProcAddress', size: 48 }, { name: 'OpenProcess', size: 56 },
  ],
  imports: [
    'kernel32.dll:VirtualAllocEx', 'kernel32.dll:CreateRemoteThread',
    'ws2_32.dll:connect', 'advapi32.dll:RegSetValueEx',
    'ntdll.dll:NtUnmapViewOfSection', 'kernel32.dll:OpenProcess',
    'ws2_32.dll:send', 'ws2_32.dll:recv',
  ],
  strings: [
    'cmd.exe /c', 'HKLM\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Run',
    'http://192.168.1.100:4444', 'powershell -enc',
    '%TEMP%\\svchost.exe', 'CreateRemoteThread',
    'User-Agent: Mozilla/5.0', '/bin/sh -i',
  ],
  calls: [
    'VirtualAllocEx', 'WriteProcessMemory', 'CreateRemoteThread',
    'OpenProcess', 'connect', 'send', 'recv', 'RegSetValueEx',
  ],
  behaviours: [
    'process_injection', 'registry_persistence',
    'network_communication', 'payload_download', 'anti_debugging',
  ],
  capabilities: [
    'process_injection', 'network_communication',
    'registry_persistence', 'anti_debugging', 'privilege_escalation',
  ],
  mitre: ['T1055', 'T1071', 'T1547', 'T1059', 'T1134'],
  risk_score: 87.4,
  cvss: 8.9,
  cvss_detail: {
    cvss_score: 8.9, risk_level: 'HIGH', technique_count: 5,
    tactic_coverage: ['Defense Evasion', 'Execution', 'Persistence', 'C2'],
  },
  verdict: {
    verdict:            'MALICIOUS',
    label:              'MALICIOUS',
    confidence:         94,
    confidence_pct:     94,
    confidence_label:   'High',
    category:           'Trojan / RAT',
    summary:            'High-confidence remote access trojan with process injection and persistent C2 communication.',
    recommended_actions: [
      'Quarantine and isolate immediately.',
      'Do not execute on any production system.',
      'Submit hash to threat intelligence feeds.',
      'Perform full endpoint scan on originating host.',
    ],
  },
  hashes: {
    md5:    'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4',
    sha1:   'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
    sha256: 'a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
  },
  file_type: 'PE Executable (EXE/DLL)',
  summary: 'High-confidence remote access trojan with process injection and persistent C2.',
  analysis_meta: {
    ghidra_available:  true,
    radare2_available: true,
    extraction_engine: 'radare2+ghidra',
    capa_enabled:      true,
    total_elapsed:     12.4,
    filename:          'demo_sample.exe',
  },
};
